import React, { useState, useRef, useEffect } from "react";
import { storage } from '../../firebase'; 
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import ViewUpload from "./ViewUploads";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import { CloudUpload, Delete, Download } from "@mui/icons-material";

const FileUpload = () => {
  const [selectedOption, setSelectedOption] = useState("");
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [date, setDate] = useState("");
  const [moduleWattage, setModuleWattage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const fileInputRef = useRef(null);
  const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks for EL uploads
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Fetch supplier names and module wattages from the API
  useEffect(() => {
    const selectedProject = localStorage.getItem("selectedProject");

    axios
      .post(`${baseURL}get_supplier_names`, {
        projectId: selectedProject,
      })
      .then((response) => {
        const supplierData = response.data.map((supplier) => ({
          name: supplier.name,
          moduleWattages: supplier.moduleWattage ? supplier.moduleWattage.split(",") : [],
        }));
        setSuppliers(supplierData);
      })
      .catch((error) => {
        console.error("Error fetching supplier data:", error);
        setErrorMessage("Failed to fetch supplier data. Please try again.");
      });
  }, []);

  const handleFileUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedOption === "el" && !selectedFile.name.endsWith(".zip")) {
        setErrorMessage("Please upload a ZIP file for EL uploads.");
        setFile(null);
        setFileName(null);
        return;
      }
      if (["flash", "pdi", "packing"].includes(selectedOption) &&
          ![".xls", ".xlsx"].some(ext => selectedFile.name.endsWith(ext))) {
        setErrorMessage("Please upload an XLS or XLSX file for this data type.");
        setFile(null);
        setFileName(null);
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setErrorMessage("");
      setSuccessMessage("");
    }
  };

  const removeFile = () => {
    setFile(null);
    setFileName(null);
    fileInputRef.current.value = null;
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleOptionChange = (e) => {
    setFileName(null);
    setSelectedOption(e.target.value);
    setShowDownloadButton(
      e.target.value === "flash" || e.target.value === "pdi" || e.target.value === "packing"
    );
    setErrorMessage("");
    setSuccessMessage("");
    setModuleWattage(""); // Reset module wattage when data type changes
  };

  const handleDateChange = (e) => {
    setDate(e.target.value);
  };

  const handleSupplierChange = (e) => {
    setSelectedSupplier(e.target.value);
    setModuleWattage(""); // Reset module wattage when supplier changes
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleModuleWattageChange = (e) => {
    setModuleWattage(e.target.value);
  };

  const handleDownloadTemplate = () => {
    let templateUrl = "";
    let downloadName = "";

    if (selectedOption === "flash") {
      templateUrl = `${process.env.PUBLIC_URL}/Templates for Daily Flash Data.xlsx`;
      downloadName = "flash_template.xlsx";
    } else if (selectedOption === "pdi") {
      templateUrl = `${process.env.PUBLIC_URL}/Template for PDI offered.xlsx`;
      downloadName = "pdi_template.xlsx";
    } else if (selectedOption === "packing") {
      templateUrl = `${process.env.PUBLIC_URL}/Template for Packing List.xlsx`;
      downloadName = "packing_template.xlsx";
    }

    if (templateUrl) {
      const link = document.createElement("a");
      link.href = templateUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const uploadWholeFile = async () => {
    const formData = new FormData();
    const projectId = localStorage.getItem("selectedProject");

    formData.append("projectId", projectId);
    formData.append("date", date);
    formData.append("selectedOption", selectedOption);
    formData.append("supplier", selectedSupplier);
    formData.append("moduleWattage", moduleWattage);
    formData.append("file", file, file.name);

    try {
      const endpoint = `${baseURL}upload_file`;
      await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          setUploadProgress(progress);
        },
      });

      setSuccessMessage("File uploaded successfully!");
      setErrorMessage("");
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        {
          name: file.name,
          dataType: selectedOption,
          date: date,
          supplier: selectedSupplier,
          moduleWattage: moduleWattage,
        },
      ]);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Upload failed. Please try again.");
      setSuccessMessage("");
      throw error;
    }
  };

  const uploadELToFirebase = async () => {
    try {
      const projectId = localStorage.getItem("selectedProject");
      const filePath = `${projectId}/${selectedSupplier}/el/${file.name}`;
      const storageRef = ref(storage, filePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(percent);
          },
          (err) => {
            setErrorMessage("Upload failed. Please try again.");
            setSuccessMessage("");
            reject(err);
          },
          async () => { // Success handler is already async
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              
              // Update UI states (your existing code)
              setSuccessMessage("EL file uploaded successfully!");
              setErrorMessage("");
              setUploadedFiles((prevFiles) => [
                ...prevFiles,
                {
                  name: file.name,
                  dataType: selectedOption,
                  date: date,
                  supplier: selectedSupplier,
                  moduleWattage: moduleWattage,
                  downloadURL: downloadURL,
                },
              ]);

              await axios.post(
                'https://el-extraction-761890394243.asia-south1.run.app',
                {
                  project_name: projectId,
                  supplier_name: selectedSupplier,
                  filename: file.name,
                }
              );
              // Optional: Update success message to include processing
              setSuccessMessage("EL file uploaded and processing triggered successfully!");

            } catch (axiosError) {
              console.error('Error sending processing request:', axiosError);
              setErrorMessage("Upload succeeded, but processing trigger failed. Please check manually.");
              // Don't override main success message
            }

            // Your existing timeout/reload
            setTimeout(() => {
              // window.location.reload(); // Uncomment if needed
            }, 2000);

            resolve(); // Resolve the upload Promise
          }
        );
      });
    } catch (err) {
      setErrorMessage("Upload failed. Please try again.");
      setSuccessMessage("");
      setIsLoading(false);
      throw err;
    }
  };

  const uploadHandler = async () => {
    if (!file || !selectedSupplier || !date) {
      setErrorMessage("Please select a file, a supplier, and date.");
      setSuccessMessage("");
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);
    setShowProgressBar(true);

    try {
      if (selectedOption === "el") {
        // Use Firebase for EL uploads
        await uploadELToFirebase();
      } else {
        // Use existing API for flash, pdi, packing
        await uploadWholeFile();
      }
    } catch (error) {
      // Error message is set in the respective upload functions
    } finally {
      setIsLoading(false);
      setShowProgressBar(false);
    }
  };

  // Get module wattages for the selected supplier
  const selectedSupplierData = suppliers.find((supplier) => supplier.name === selectedSupplier);
  const moduleWattageOptions = selectedSupplierData?.moduleWattages || [];

  const isUploadDisabled = !date || !fileName || !selectedSupplier || (["flash", "pdi"].includes(selectedOption) && !moduleWattage);

  return (
    <div className="flex items-center justify-center p-2">
      <div className="w-full bg-white rounded-xl shadow-2xl p-8 space-y-8">
        <h1 className="text-3xl font-bold text-gray-800 text-center">File Upload System</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label className="text-gray-700 font-medium mb-2">Select Supplier</label>
            <select
              value={selectedSupplier}
              onChange={handleSupplierChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-150 ease-in-out"
            >
              <option value="" disabled>Select a supplier</option>
              {suppliers.map((supplier, index) => (
                <option key={index} value={supplier.name}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-gray-700 font-medium mb-2">Select Data Type</label>
            <select
              value={selectedOption}
              onChange={handleOptionChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-150 ease-in-out"
            >
              <option value="" disabled>Select a Data Type</option>
              <option value="flash">Daily Flash Data</option>
              <option value="pdi">PDI Offered</option>
              <option value="el">EL Image</option>
              <option value="packing">Packing List</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label className="text-gray-700 font-medium mb-2">Date of Data</label>
            <input
              type="date"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-150 ease-in-out"
              value={date}
              max={new Date().toISOString().split("T")[0]}
              onChange={handleDateChange}
            />
          </div>

          {["flash", "pdi"].includes(selectedOption) && (
            <div className="flex flex-col">
              <label className="text-gray-700 font-medium mb-2">Module Wattage (Wp)</label>
              <select
                value={moduleWattage}
                onChange={handleModuleWattageChange}
                className="w-full h-[3rem] px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-150 ease-in-out"
                disabled={!selectedSupplier || moduleWattageOptions.length === 0}
              >
                <option value="" disabled>Select module wattage</option>
                {moduleWattageOptions.map((wattage, index) => (
                  <option key={index} value={wattage}>
                    {wattage} Wp
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {showDownloadButton && (
          <div className="flex justify-center">
            <button
              className="flex items-center px-6 py-3 bg-[#424752] text-white rounded-lg shadow-lg hover:bg-gray-600 transition duration-150 ease-in-out"
              onClick={handleDownloadTemplate}
            >
              <Download className="mr-2" />
              Download {selectedOption === "flash" ? "Flash" : selectedOption === "pdi" ? "PDI Offered" : "Packing List"} Template
            </button>
          </div>
        )}

        <div className="mt-6">
          <label className="text-gray-700 font-medium mb-2 flex flex-col space-y-1">
            <span className="flex items-center">
              {selectedOption.replace("_", " ").toUpperCase()} Upload
              <span className="text-gray-700 font-medium flex flex-col space-y-1">
                {["pdi", "flash", "packing"].includes(selectedOption.toLowerCase())
                  ? " (.xlsx and .xls only)"
                  : selectedOption.toLowerCase() === "el"
                    ? " (.zip only)"
                    : ""}
              </span>
            </span>
          </label>

          <div className="flex items-center space-x-4">
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileUpload}
              accept={
                ["pdi", "flash", "packing"].includes(selectedOption.toLowerCase())
                  ? ".xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  : selectedOption.toLowerCase() === "el"
                    ? ".zip"
                    : ""
              }
            />

            <button
              className="flex items-center px-6 py-3 bg-[#424752] text-white rounded-lg shadow-lg hover:bg-gray-600 transition duration-150 ease-in-out"
              onClick={() => fileInputRef.current.click()}
            >
              <CloudUpload className="mr-2" />
              Browse Files
            </button>

            <span className="truncate text-gray-600">
              {fileName || "No file selected"}
            </span>

            {fileName && (
              <button
                className="text-red-500 hover:text-red-700 transition duration-150 ease-in-out"
                onClick={removeFile}
              >
                <Delete />
              </button>
            )}
          </div>

          {(errorMessage || successMessage) && (
            <div className="flex justify-center items-center mt-2">
              <div
                className={`flex items-center px-4 py-2 rounded-lg shadow-md ${
                  errorMessage ? "bg-red-100" : "bg-green-100"
                }`}
              >
                <svg
                  className={`w-5 h-5 ${errorMessage ? "text-red-500" : "text-green-500"} mr-2`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  <path d="M12 8v4" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                <p className={`italic ${errorMessage ? "text-red-500" : "text-green-500"}`}>
                  {errorMessage || successMessage}
                </p>
              </div>
            </div>
          )}
        </div>

        {showProgressBar && (
          <div className="mt-6">
            <progress value={uploadProgress} max="100" className="w-full" />
            <p className="text-center text-gray-600">{uploadProgress.toFixed(2)}%</p>
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <button
            className={`flex items-center px-16 py-4 rounded-lg text-white font-semibold ${
              isUploadDisabled ? "bg-gray-400 cursor-not-allowed" : "bg-[#424752]"
            } shadow-lg transition duration-150 ease-in-out`}
            onClick={isUploadDisabled ? null : uploadHandler}
            disabled={isLoading || isUploadDisabled}
          >
            {isLoading ? (
              <div className="flex items-center">
                <CircularProgress size={24} className="text-white mr-2" />
                Processing...
              </div>
            ) : (
              "Upload"
            )}
          </button>
        </div>

        <ViewUpload uploadedFiles={uploadedFiles} />
      </div>
    </div>
  );
};

export default FileUpload;

// import React, { useState, useRef, useEffect } from "react";
// import Snackbar from "@mui/material/Snackbar";
// import MuiAlert from "@mui/material/Alert";
// import ViewUpload from "./ViewUploads";
// import axios from "axios";
// import CircularProgress from "@mui/material/CircularProgress";
// import { CloudUpload, Delete, Download, Cancel } from "@mui/icons-material";

// const Alert = React.forwardRef(function Alert(props, ref) {
//   return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
// });

// const FileUpload = () => {
//   const [selectedOption, setSelectedOption] = useState("flash");
//   const [file, setFile] = useState(null);
//   const [fileName, setFileName] = useState(null);
//   const [date, setDate] = useState("");
//   const [moduleWattage, setModuleWattage] = useState("");
//   const [successMessage, setSuccessMessage] = useState("");
//   const [openSnackbar, setOpenSnackbar] = useState(false);
//   const [uploadedFiles, setUploadedFiles] = useState([]);
//   const [suppliers, setSuppliers] = useState([]);
//   const [selectedSupplier, setSelectedSupplier] = useState("");
//   const [isLoading, setIsLoading] = useState(false);
//   const [isError, setIsError] = useState(false);
//   const [cancelTokenSource, setCancelTokenSource] = useState(null);
//   const [showDownloadButton, setShowDownloadButton] = useState(false);
//   const [uploadProgress, setUploadProgress] = useState(0); // Track upload progress
//   const [showProgressBar, setShowProgressBar] = useState(false); // Control progress bar visibility
//   const fileInputRef = useRef(null);

//   //const CHUNK_SIZE = 10 * 1024 * 1024; // 100MB per chunk

//   // Fetch supplier names from the API
//   useEffect(() => {
//     const selectedProject = localStorage.getItem("selectedProject");

//     axios
//       .post("https://redoms-backend-dev.m-devsecops.com/get_supplier_names", {
//         projectId: selectedProject,
//       })
//       .then((response) => {
//         const supplierData = response.data.map((supplier) => ({
//           name: supplier.name,
//         }));
//         setSuppliers(supplierData);
//       })
//       .catch((error) => console.error("Error fetching supplier data:", error));
//   }, []);

//   const handleFileUpload = (e) => {
//     setFile(e.target.files[0]);
//     setFileName(e.target.files[0]?.name || null);
//   };

//   const removeFile = () => {
//     setFile(null);
//     setFileName(null);
//     fileInputRef.current.value = null;
//   };

//   const handleOptionChange = (e) => {
//     setFileName(null);
//     setSelectedOption(e.target.value);
//     setShowDownloadButton(
//       e.target.value === "flash" || e.target.value === "pdi" || e.target.value === "packing"
//     );
//   };

//   const handleDateChange = (e) => {
//     setDate(e.target.value);
//   };

//   const handleSupplierChange = (e) => {
//     setSelectedSupplier(e.target.value);
//   };

//   const handleModuleWattageChange = (e) => {
//     setModuleWattage(e.target.value);
//   };

//   const handleDownloadTemplate = () => {
//     let templateUrl = "";
//     let downloadName = "";

//     if (selectedOption === "flash") {
//       templateUrl = `${process.env.PUBLIC_URL}/Templates for Daily Flash Data.xlsx`;
//       downloadName = "flash_template.xlsx";
//     } else if (selectedOption === "pdi") {
//       templateUrl = `${process.env.PUBLIC_URL}/Template for PDI offered.xlsx`;
//       downloadName = "pdi_template.xlsx";
//     } else if (selectedOption === "packing") {
//       templateUrl = `${process.env.PUBLIC_URL}/Template for Packing List.xlsx`;
//       downloadName = "packing_template.xlsx";
//     }

//     if (templateUrl) {
//       // Create a temporary anchor element to trigger the download
//       const link = document.createElement("a");
//       link.href = templateUrl;
//       link.download = downloadName;
//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);
//     }
//   };

//   const uploadChunk = async (chunk, chunk_index, total_chunks, fileName) => {
//     const formData = new FormData();
//     const projectId = localStorage.getItem("selectedProject");
//     const today = new Date();
//     const formattedDate = `${today.getDate().toString().padStart(2, '0')}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getFullYear()}`;


//     formData.append("projectId", projectId);
//     // formData.append("fileName", fileName);
//     formData.append("date", date);
//     // formData.append("currentDate", formattedDate);
//     formData.append("selectedOption", selectedOption);
//     formData.append("supplier", selectedSupplier);
//     formData.append("chunk_index", chunk_index);
//     formData.append("total_chunks", total_chunks);

//     if (["flash", "pdi", "packing"].includes(selectedOption)) {
//       formData.append("moduleWattage", moduleWattage);
//     }

//     formData.append("file", chunk, fileName);

//     try {
//       let endpoint = "https://redoms-backend-dev.m-devsecops.com/upload_file";
//       if (selectedOption === "el") {
//         endpoint = "https://upload-from-frontend-zip-file-761890394243.asia-south1.run.app/upload_el_image_zip";
//       }

//       await axios.post(endpoint, formData, {
//         headers: {
//           "Content-Type": "multipart/form-data",
//         },
//         cancelToken: cancelTokenSource?.token,
//       });

//       // Update progress
//       setUploadProgress(((chunk_index + 1) / total_chunks) * 100);
//     } catch (error) {
//       if (axios.isCancel(error)) {
//         setSuccessMessage("Upload cancelled");
//         setOpenSnackbar(true);
//       } else {
//         console.error(`Error uploading chunk ${chunk_index}:`, error);
//         setSuccessMessage("Upload failed. Please try again.");
//         setIsError(true);
//         setOpenSnackbar(true);
//         throw error; // Stop further uploads
//       }
//     }
//   };

//   const uploadHandler = async () => {
//     if (!file || !selectedSupplier) {
//       alert("Please select a file and a supplier.");
//       return;
//     }

//     const source = axios.CancelToken.source();
//     setCancelTokenSource(source);
//     setIsLoading(true);
//     setUploadProgress(0);
//     setShowProgressBar(true); // Show progress bar after upload starts

//     let CHUNK_SIZE;
//     if (["flash", "pdi", "packing"].includes(selectedOption)) {
//       CHUNK_SIZE = 1 * 1024 * 1024; // 1 MB chunks
//     } else {
//       CHUNK_SIZE = 10 * 1024 * 1024; // Default fallback
//     }

//     const total_chunks = Math.ceil(file.size / CHUNK_SIZE);

//     try {
//       for (let chunk_index = 0; chunk_index < total_chunks; chunk_index++) {
//         const start = chunk_index * CHUNK_SIZE;
//         const end = Math.min(start + CHUNK_SIZE, file.size);
//         const chunk = file.slice(start, end);

//         await uploadChunk(chunk, chunk_index, total_chunks, file.name);
//       }

//       setSuccessMessage("File uploaded successfully!");
//       setIsError(false);
//       setOpenSnackbar(true);
//       setUploadedFiles((prevFiles) => [
//         ...prevFiles,
//         {
//           name: file.name,
//           dataType: selectedOption,
//           date: date,
//           supplier: selectedSupplier,
//           moduleWattage: moduleWattage,
//         },
//       ]);
//     } catch (error) {
//       if (axios.isCancel(error)) {
//         setSuccessMessage("Upload cancelled");
//       } else {
//         setSuccessMessage("Upload failed. Please try again.");
//         setIsError(true);
//       }
//       setOpenSnackbar(true);
//     } finally {
//       setIsLoading(false);
//       setCancelTokenSource(null);
//       setShowProgressBar(false); // Hide progress bar after upload ends
//     }
//   };

//   const cancelUpload = () => {
//     if (cancelTokenSource) {
//       cancelTokenSource.cancel();
//       setIsLoading(false);
//       setShowProgressBar(false); // Hide progress bar on cancel
//     }
//   };

//   const handleCloseSnackbar = (event, reason) => {
//     if (reason === "clickaway") {
//       return;
//     }
//     setOpenSnackbar(false);
//   };

//   const isUploadDisabled = !date || !fileName || !selectedSupplier;

//   return (
//     <div className="flex items-center justify-center p-2">
//       <div className="w-full bg-white rounded-xl shadow-2xl p-8 space-y-8">
//         {/* Header */}
//         <h1 className="text-3xl font-bold text-gray-800 text-center">
//           File Upload System
//         </h1>

//         {/* Supplier and Module Wattage Section */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           <div className="flex flex-col">
//             <label className="text-gray-700 font-medium mb-2">Select Supplier</label>
//             <select
//               value={selectedSupplier}
//               onChange={handleSupplierChange}
//               className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-150 ease-in-out"
//             >
//               <option value="" disabled>Select a supplier</option>
//               {suppliers.map((supplier, index) => (
//                 <option key={index} value={supplier.name}>
//                   {supplier.name}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <div className="flex flex-col">
//             <label className="text-gray-700 font-medium mb-2">Select Data Type</label>
//             <select
//               value={selectedOption}
//               onChange={handleOptionChange}
//               className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-150 ease-in-out"
//             >
//               <option value="flash">Daily Flash Data</option>
//               <option value="pdi">PDI Offered</option>
//               <option value="el">EL Image</option>
//               <option value="packing">Packing List</option>
//             </select>
//           </div>
//         </div>

//         {/* Data Type and Date Section */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

//           <div className="flex flex-col">
//             <label className="text-gray-700 font-medium mb-2">Date of Data</label>
//             <input
//               type="date"
//               className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-150 ease-in-out"
//               value={date}
//               max={new Date().toISOString().split("T")[0]}
//               onChange={handleDateChange}
//             />
//           </div>

//           {["flash", "pdi"].includes(selectedOption) && (
//             <div className="flex flex-col">
//               <label className="text-gray-700 font-medium mb-2">Module Wattage (Wp)</label>
//               <input
//                 type="number"
//                 className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring transition duration-150 ease-in-out"
//                 value={moduleWattage}
//                 onChange={handleModuleWattageChange}
//               />
//             </div>
//           )}
//         </div>

//         {/* Download Template Button */}
//         {showDownloadButton && (
//           <div className="flex justify-center">
//             <button
//               className="flex items-center px-6 py-3 bg-[#424752] text-white rounded-lg shadow-lg hover:bg-gray-600 transition duration-150 ease-in-out"
//               onClick={handleDownloadTemplate}
//             >
//               <Download className="mr-2" />
//               Download {selectedOption === "flash" ? "Flash" : selectedOption === "pdi" ? "PDI Offered" : "Packing List"} Template
//             </button>
//           </div>
//         )}

//         {/* File Upload Section */}
//         <div className="mt-6">
//           <label className="text-gray-700 font-medium mb-2 flex flex-col space-y-1">
//             <span className="flex items-center">
//               {selectedOption.replace("_", " ").toUpperCase()} Upload
//               <span className="text-gray-700 font-medium flex flex-col space-y-1">
//                 {["pdi", "flash", "packing list"].includes(selectedOption.toLowerCase())
//                   ? " (.xlsx and.xlx only)"
//                   : selectedOption.toLowerCase() === "el"
//                     ? " (.zip Only)"
//                     : ""}
//               </span>
//             </span>
//           </label>

//           {/* File Upload Input */}
//           <div className="flex items-center space-x-4">
//             <input
//               type="file"
//               ref={fileInputRef}
//               style={{ display: "none" }}
//               onChange={handleFileUpload}
//               accept={
//                 ["pdi", "flash", "packing list"].includes(selectedOption.toLowerCase())
//                   ? ".xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
//                   : selectedOption.toLowerCase() === "el"
//                     ? ".zip"
//                     : ""
//               }
//             />

//             <button
//               className="flex items-center px-6 py-3 bg-[#424752] text-white rounded-lg shadow-lg hover:bg-gray-600 transition duration-150 ease-in-out"
//               onClick={() => fileInputRef.current.click()}
//             >
//               <CloudUpload className="mr-2" />
//               Browse Files
//             </button>

//             <span className="truncate text-gray-600">
//               {fileName || "No file selected"}
//             </span>

//             {fileName && (
//               <button
//                 className="text-red-500 hover:text-red-700 transition duration-150 ease-in-out"
//                 onClick={removeFile}
//               >
//                 <Delete />
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Upload Progress */}
//         {showProgressBar && (
//           <div className="mt-6">
//             <progress value={uploadProgress} max="100" className="w-full" />
//             <p className="text-center text-gray-600">{uploadProgress.toFixed(2)}%</p>
//           </div>
//         )}

//         {/* Upload Button */}
//         <div className="mt-6 flex justify-center">
//           <button
//             className={`flex items-center px-16 py-4 rounded-lg text-white font-semibold ${isUploadDisabled
//               ? "bg-gray-400 cursor-not-allowed"
//               : "bg-[#424752]"
//               } shadow-lg transition duration-150 ease-in-out`}
//             onClick={isUploadDisabled ? null : uploadHandler}
//             disabled={isLoading || isUploadDisabled}
//           >
//             {isLoading ? (
//               <div className="flex items-center">
//                 <CircularProgress size={24} className="text-white mr-2" />
//                 Processing...
//               </div>
//             ) : (
//               "Upload"
//             )}
//           </button>
//         </div>

//         {/* Snackbar */}
//         <Snackbar
//           open={openSnackbar}
//           autoHideDuration={4000}
//           onClose={handleCloseSnackbar}
//         >
//           <Alert onClose={handleCloseSnackbar} severity={isError ? "error" : "success"}>
//             {successMessage}
//           </Alert>
//         </Snackbar>

//         {/* Uploaded files */}
//         <ViewUpload uploadedFiles={uploadedFiles} />
//       </div>
//     </div>
//   );
// };

// export default FileUpload;